// Super-admin /super/alerts — the system-scope alert console.
//
// Pulls /api/user/v1/notification (which, for platform admins, returns
// system-scope rows with organisationId NULL — ENGINE_HEALTH and
// friends — alongside any org-scoped rows). Renders 4 StatTiles +
// filter chips + a paginated table + a click-row detail drawer.
//
// Polls every 30s. The bell in the SuperAdmin topbar polls
// independently — they don't share state, but they hit the same
// endpoint, so a mark-read in either surface is visible in the other
// after the next 30s tick.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Button,
  Stack,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";

import {
  TOKENS,
  PageHeader,
  StatTile,
  SectionCard,
  EmptyState,
  DetailDrawer,
  Section,
  RowMenu,
} from "../../../components/pages/_new/super/design";
import {
  SeverityChip,
  severityFor,
  relativeTime,
} from "../../../components/pages/_new/super/NotificationBell";
import { ReactComponent as AlertIcon } from "../../../vendor/_new/icon/navbar/notification.svg";
import {
  getNotifications,
  markRead,
  markAllRead,
} from "../../../services/notificationsService";

const POLL_MS = 30000;
const PAGE_SIZE = 25;

const thSx = {
  padding: "10px 14px",
  borderBottom: `1px solid ${TOKENS.border}`,
  background: TOKENS.bgSunken,
  color: TOKENS.fg2,
  fontSize: 11.5,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};
const tdSx = {
  padding: "12px 14px",
  borderBottom: `1px solid ${TOKENS.borderSubtle}`,
  fontSize: 13,
  color: TOKENS.fg1,
  verticalAlign: "top",
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "high", label: "High" },
  { key: "medium", label: "Medium" },
  { key: "low", label: "Low" },
  { key: "unread", label: "Unread only" },
];

const FilterChip = ({ active, label, count, onClick }) => (
  <Box
    onClick={onClick}
    role="button"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      height: 30,
      px: "12px",
      borderRadius: 999,
      border: `1px solid ${active ? TOKENS.primary : TOKENS.border}`,
      background: active ? TOKENS.primaryTint : TOKENS.bgSurface,
      color: active ? TOKENS.primaryInk : TOKENS.fg1,
      fontSize: 12.5,
      fontWeight: 600,
      cursor: "pointer",
      transition: "background 120ms ease, border-color 120ms ease",
      "&:hover": { borderColor: TOKENS.primary, background: TOKENS.primaryTint },
    }}
  >
    {label}
    {typeof count === "number" ? (
      <Box
        component="span"
        sx={{
          fontSize: 11,
          color: active ? TOKENS.primaryInk : TOKENS.fg3,
          fontWeight: 500,
        }}
      >
        {count}
      </Box>
    ) : null}
  </Box>
);

const SuperAdminAlerts = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [serverTotal, setServerTotal] = useState(0);
  const [drawerRow, setDrawerRow] = useState(null);

  const mountedRef = useRef(true);

  const fetchOnce = useCallback(async () => {
    try {
      // Severity/unread filters are derived client-side — load full list then.
      // Default "all" uses server page/limit.
      const res = filter === "all"
        ? await getNotifications({ page, limit: PAGE_SIZE })
        : await getNotifications();
      const data = res?.data ?? res;
      if (mountedRef.current) {
        if (data && Array.isArray(data.items)) {
          setItems(data.items);
          setServerTotal(typeof data.total === "number" ? data.total : data.items.length);
        } else {
          const list = Array.isArray(data) ? data : [];
          setItems(list);
          setServerTotal(list.length);
        }
        setError(null);
      }
    } catch (e) {
      if (mountedRef.current) setError(typeof e === "string" ? e : e?.message || "Failed to load");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    mountedRef.current = true;
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [fetchOnce]);

  // ---------------- Derived state ----------------
  const enriched = useMemo(
    () => items.map((n) => ({ ...n, _sev: severityFor(n.type) })),
    [items]
  );

  const totals = useMemo(() => {
    const out = {
      total: filter === "all" ? serverTotal : enriched.length,
      unread: 0,
      high24: 0,
    };
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    for (const n of enriched) {
      if (!n.read) out.unread += 1;
      if (n._sev === "high" && new Date(n.created || 0).getTime() >= cutoff) {
        out.high24 += 1;
      }
    }
    return out;
  }, [enriched, filter, serverTotal]);

  const countsByFilter = useMemo(() => {
    const counts = {
      all: filter === "all" ? serverTotal : enriched.length,
      high: 0,
      medium: 0,
      low: 0,
      unread: 0,
    };
    for (const n of enriched) {
      if (n._sev === "high") counts.high += 1;
      else if (n._sev === "medium") counts.medium += 1;
      else counts.low += 1;
      if (!n.read) counts.unread += 1;
    }
    return counts;
  }, [enriched, filter, serverTotal]);

  const filtered = useMemo(() => {
    let rows = enriched;
    if (filter === "unread") rows = rows.filter((n) => !n.read);
    else if (filter !== "all") rows = rows.filter((n) => n._sev === filter);
    return rows
      .slice()
      .sort((a, b) => new Date(b.created || 0) - new Date(a.created || 0));
  }, [enriched, filter]);

  const pageRows = useMemo(() => {
    if (filter === "all") return filtered;
    return filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  }, [filtered, page, filter]);

  useEffect(() => {
    // Reset to page 0 when the filter changes.
    setPage(0);
  }, [filter]);

  // ---------------- Mark-read actions ----------------
  const handleMarkOne = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try {
      await markRead(id);
    } catch (_e) {
      /* re-fetch will reconcile */
    }
    fetchOnce();
  };

  const handleMarkAll = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await markAllRead();
    } catch (_e) {
      /* re-fetch will reconcile */
    }
    fetchOnce();
  };

  const handleRowClick = (row) => {
    setDrawerRow(row);
    if (!row.read) handleMarkOne(row.id);
  };

  // ---------------- Render ----------------
  const listCount = filter === "all" ? serverTotal : filtered.length;
  const totalPages = Math.max(1, Math.ceil(listCount / PAGE_SIZE));

  return (
    <Box>
      <Helmet title="Alerts" />
      <PageHeader
        title="Alerts"
        subtitle={
          loading
            ? "Loading…"
            : `${totals.total} total · ${totals.unread} unread`
        }
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<MarkEmailReadOutlinedIcon fontSize="small" />}
              disabled={totals.unread === 0 || loading}
              onClick={handleMarkAll}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: TOKENS.fg1,
                background: TOKENS.bgSurface,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: `${TOKENS.radiusXs}px`,
                px: 1.5,
                "&:hover": { borderColor: TOKENS.primary, background: TOKENS.bgSunken },
              }}
            >
              Mark all read
            </Button>
            <Button
              size="small"
              startIcon={<RefreshIcon fontSize="small" />}
              disabled={loading}
              onClick={fetchOnce}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#fff",
                background: TOKENS.primary,
                borderRadius: `${TOKENS.radiusXs}px`,
                px: 1.5,
                "&:hover": { background: TOKENS.primaryHover },
              }}
            >
              Refresh
            </Button>
          </Stack>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<NotificationsOutlinedIcon />}
            value={totals.total}
            label="Total alerts"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<NotificationsNoneIcon />}
            iconBg={TOKENS.infoBg}
            iconInk={TOKENS.infoInk}
            value={totals.unread}
            label="Unread"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<PriorityHighIcon />}
            iconBg={TOKENS.dangerBg}
            iconInk={TOKENS.dangerInk}
            value={totals.high24}
            label="High severity"
            sub="Last 24h"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<ReportGmailerrorredIcon />}
            iconBg={TOKENS.warningBg}
            iconInk={TOKENS.warningInk}
            // Engines-degraded count would require a separate engines-summary
            // fetch + cross-product over Pingora/BM/AD clusters. Left as a
            // dash until the engines service exposes a one-shot summary the
            // alerts page can consume without N round-trips.
            value="—"
            label="Engines degraded"
            sub="Live count"
          />
        </Grid>
      </Grid>

      <SectionCard
        title="Recent alerts"
        description="System-scope and platform-admin notifications across all organisations."
      >
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mb: 2 }}>
          {FILTERS.map((f) => (
            <FilterChip
              key={f.key}
              active={filter === f.key}
              label={f.label}
              count={countsByFilter[f.key]}
              onClick={() => setFilter(f.key)}
            />
          ))}
        </Stack>

        {error ? (
          <EmptyState
            icon={<ReportGmailerrorredIcon />}
            title="Couldn't load alerts"
            body={String(error)}
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<NotificationsNoneIcon />}
            title="No alerts match the current filter"
            body="System-scope alerts appear here as they fire — engines going stale, license breaches, posture findings, quota requests."
          />
        ) : (
          <>
            <Box sx={{ border: `1px solid ${TOKENS.borderSubtle}`, borderRadius: `${TOKENS.radiusXs}px`, overflow: "hidden" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...thSx, width: 110 }}>Severity</TableCell>
                    <TableCell sx={thSx}>Title</TableCell>
                    <TableCell sx={{ ...thSx, width: 180 }}>Type</TableCell>
                    <TableCell sx={{ ...thSx, width: 140 }}>When</TableCell>
                    <TableCell sx={{ ...thSx, width: 110 }}>Source</TableCell>
                    <TableCell sx={{ ...thSx, width: 60 }}>{""}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((row) => {
                    const when = row.created ? new Date(row.created) : null;
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => handleRowClick(row)}
                        sx={{
                          cursor: "pointer",
                          background: row.read ? "transparent" : TOKENS.primaryTint,
                          "& td:first-of-type": { position: "relative" },
                          "&:hover": { background: TOKENS.bgSunken },
                        }}
                      >
                        <TableCell sx={tdSx}>
                          <SeverityChip severity={row._sev} />
                        </TableCell>
                        <TableCell sx={tdSx}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: row.read ? 400 : 600,
                              color: TOKENS.fg1,
                              mb: 0.25,
                            }}
                          >
                            {row.title || "Notification"}
                          </Typography>
                          {row.content ? (
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: TOKENS.fg2,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 520,
                              }}
                            >
                              {row.content}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ ...tdSx, color: TOKENS.fg2 }}>
                          {row.type && row.type !== "DEFAULT"
                            ? String(row.type).replace(/_/g, " ")
                            : "—"}
                        </TableCell>
                        <TableCell sx={{ ...tdSx, color: TOKENS.fg2 }}>
                          {when ? (
                            <Tooltip title={when.toLocaleString()}>
                              <span>{relativeTime(row.created)}</span>
                            </Tooltip>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell sx={{ ...tdSx, color: TOKENS.fg2 }}>System</TableCell>
                        <TableCell sx={tdSx}>
                          <RowMenu
                            actions={[
                              {
                                key: "mark",
                                label: row.read ? "Already read" : "Mark read",
                                icon: <MarkEmailReadOutlinedIcon fontSize="small" />,
                                onClick: () => !row.read && handleMarkOne(row.id),
                              },
                              {
                                key: "view",
                                label: "View detail",
                                icon: <NotificationsOutlinedIcon fontSize="small" />,
                                onClick: () => handleRowClick(row),
                              },
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {totalPages > 1 ? (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mt: 2 }}
              >
                <Typography sx={{ fontSize: 12, color: TOKENS.fg3 }}>
                  Showing {page * PAGE_SIZE + 1}–
                  {Math.min(listCount, (page + 1) * PAGE_SIZE)} of {listCount}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    sx={{
                      textTransform: "none",
                      color: TOKENS.fg1,
                      border: `1px solid ${TOKENS.border}`,
                      borderRadius: `${TOKENS.radiusXs}px`,
                      "&:hover": { borderColor: TOKENS.primary },
                    }}
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    sx={{
                      textTransform: "none",
                      color: TOKENS.fg1,
                      border: `1px solid ${TOKENS.border}`,
                      borderRadius: `${TOKENS.radiusXs}px`,
                      "&:hover": { borderColor: TOKENS.primary },
                    }}
                  >
                    Next
                  </Button>
                </Stack>
              </Stack>
            ) : null}
          </>
        )}
      </SectionCard>

      <DetailDrawer
        open={Boolean(drawerRow)}
        onClose={() => setDrawerRow(null)}
        title={drawerRow?.title || "Alert"}
        subtitle={
          drawerRow?.created
            ? new Date(drawerRow.created).toLocaleString()
            : "Notification detail"
        }
        icon={<AlertIcon />}
        headerChips={
          drawerRow
            ? [
                <SeverityChip key="sev" severity={drawerRow._sev} />,
                drawerRow.type && drawerRow.type !== "DEFAULT" ? (
                  <Box
                    key="type"
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      height: 22,
                      px: "8px",
                      borderRadius: 999,
                      background: TOKENS.bgSunken,
                      color: TOKENS.fg2,
                      border: `1px solid ${TOKENS.border}`,
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {String(drawerRow.type).replace(/_/g, " ")}
                  </Box>
                ) : null,
              ].filter(Boolean)
            : []
        }
      >
        {drawerRow ? (
          <>
            <Section label="Message">
              <Typography
                sx={{
                  fontSize: 13,
                  color: TOKENS.fg1,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {drawerRow.content || "(no body)"}
              </Typography>
            </Section>
            <Section label="Status">
              <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>
                {drawerRow.read ? "Read" : "Unread"}
              </Typography>
            </Section>
            <Section label="Source">
              <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>
                System (organisation-wide)
              </Typography>
            </Section>
            <Section label="Raw">
              <Box
                component="pre"
                sx={{
                  m: 0,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 11.5,
                  color: TOKENS.fg2,
                  background: TOKENS.bgSunken,
                  border: `1px solid ${TOKENS.borderSubtle}`,
                  borderRadius: `${TOKENS.radiusXs}px`,
                  p: 1.25,
                  overflowX: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {JSON.stringify(
                  {
                    id: drawerRow.id,
                    title: drawerRow.title,
                    type: drawerRow.type,
                    created: drawerRow.created,
                    read: drawerRow.read,
                  },
                  null,
                  2
                )}
              </Box>
            </Section>
          </>
        ) : null}
      </DetailDrawer>
    </Box>
  );
};

export default SuperAdminAlerts;
